import React, { useState, useEffect } from 'react';
import { API_URL } from '../config/apiConfig';
import {
    Box,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
    Card,
    CardContent,
    Divider,
    Chip,
    Grid,
    Collapse,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Select,
    MenuItem,
    FormControlLabel,
    Checkbox,
    FormControl,
    InputLabel,
    useMediaQuery,
    useTheme,

} from '@mui/material';
import { getPlans, assignPlanToUser, updateUserPlan, deleteUserPlan } from '../api/planApi';
import { getAffiliateFamilyMembers } from '../api/familyApi';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import DateRangeIcon from '@mui/icons-material/DateRange';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import IconButton from "@mui/material/IconButton";
import { formatRegistrationScheduleForDisplay } from "../utils/planSchedule";
import { formatCurrency } from "../utils/currency";
import { diffDateKeys, localDateToDateKey } from "../utils/affiliateTimezone";

const ActivePlans = ({ userId, affiliateId }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const [plans, setPlans] = useState([]);
    const [affiliatePlans, setAffiliatePlans] = useState([]);
    const [familyMembers, setFamilyMembers] = useState([]);
    const [selectedPlanId, setSelectedPlanId] = useState('');
    const [selectedFamilyMemberId, setSelectedFamilyMemberId] = useState('');
    const [assignToChildren, setAssignToChildren] = useState(false);
    const [openModal, setOpenModal] = useState(false);
    const [openRow, setOpenRow] = useState(null);

    // Delete confirmation modal state
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [planToDelete, setPlanToDelete] = useState(null);

    const [isPlanStartDate, setIsPlanStartDate] = useState(false);
    const [planStartDate, setPlanStartDate] = useState('');

    // Edit mode state
    const [editRowId, setEditRowId] = useState(null);
    const [editData, setEditData] = useState({
        sessionsLeft: '',
        startDate: '',
        endDate: '',
        paymentHoliday: false,
    });

    const token = localStorage.getItem('token');
    // API_URL imported from config
    const role = localStorage.getItem('role');

    const isPlanActiveByEndDate = (plan) => {
        const endDateKey = localDateToDateKey(plan.endDate);
        const todayKey = localDateToDateKey(new Date());
        return Boolean(endDateKey && todayKey && endDateKey >= todayKey);
    };


// Load user's active plans
    const loadUserActivePlans = () => {
        fetch(`${API_URL}/user/user-purchase-history?userId=${userId}&affiliateId=${affiliateId}`, {
            headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json'
            },
        })
            .then((res) => res.json())
            .then((data) => {
                // Filter active plans: end date is today or later, and sessions remain
                const active = data.filter((p) =>
                    isPlanActiveByEndDate(p) &&
                    (p.sessionsLeft > 0 || p.sessionsLeft === 9999)
                );
                setPlans(active);
            })
            .catch((err) => console.log('Error loading active plans:', err));
    };

    // Load family members
    const loadFamilyMembers = async () => {
        try {
            const members = await getAffiliateFamilyMembers(userId);
            setFamilyMembers(members);
        } catch (error) {
            console.error('Error loading family members:', error);
        }
    };

    // Initial loading
    useEffect(() => {
        loadUserActivePlans();
        loadFamilyMembers();
    }, [token, userId, affiliateId]);

    // Load affiliate plans
    useEffect(() => {
        getPlans().then((allPlans) => {
            setAffiliatePlans(allPlans);
        });
    }, []);

    // Row click handler for expansion
    const handleRowClick = (id) => {
        if (openRow === id) {
            setOpenRow(null);
        } else {
            setOpenRow(id);
        }
    };

    // Format date for display
    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Intl.DateTimeFormat('et-EE', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        }).format(new Date(dateString));
    };

    const toDateInputValue = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        if (Number.isNaN(date.getTime())) return '';
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const planHasContract = (plan) => plan?.contractId != null && Number(plan.contractId) > 0;

    // Parse training types from JSON string
    const parseTrainingTypes = (trainingTypeString) => {
        try {
            return JSON.parse(trainingTypeString);
        } catch (e) {
            return [];
        }
    };

    // Calculate days left until expiration
    const calculateDaysLeft = (plan) => {
        const diffDays = diffDateKeys(localDateToDateKey(new Date()), localDateToDateKey(plan.endDate));
        return diffDays == null ? 0 : Math.max(diffDays, 0);
    };

    const getPlanValidityText = (plan) => {
        if (plan.singleCalendarDayValidity) {
            return "1 calendar day";
        }

        if (plan.exactMonthValidity) {
            return "1 month";
        }
        return `${plan.validityDays} days`;
    };

    const getPlanCurrency = (plan) => plan?.paymentCurrency || plans[0]?.paymentCurrency || "EUR";
    const formatPlanPrice = (price, plan) => formatCurrency(price, getPlanCurrency(plan));

    // Modal handlers
    const handleOpenModal = () => {
        setOpenModal(true);
    };

    const handleCloseModal = () => {
        setOpenModal(false);
        setSelectedPlanId('');
        setSelectedFamilyMemberId('');
        setAssignToChildren(false);
        setIsPlanStartDate(false);      // Add this line
        setPlanStartDate('');           // Add this line
    };

    const handleSaveModal = async () => {
        if (!selectedPlanId) return;

        // Validate plan start date if checkbox is checked
        if (isPlanStartDate && !planStartDate) {
            alert("Please select a start date or uncheck the custom start date option.");
            return;
        }

        // If assigning to children, include familyMemberId in the request
        const familyMemberId = assignToChildren ? selectedFamilyMemberId : null;

        await assignPlanToUser(
            Number(selectedPlanId),
            Number(userId),
            affiliateId,
            familyMemberId,
            isPlanStartDate || false,
            planStartDate || 0
        );
        handleCloseModal();
        loadUserActivePlans();
    };

    // Handle checkbox change
    const handleAssignToChildrenChange = (event) => {
        setAssignToChildren(event.target.checked);
        if (!event.target.checked) {
            setSelectedFamilyMemberId('');
        }
    };

    // Delete handlers
    const handleDelete = (e, plan) => {
        e.stopPropagation(); // Prevent row expansion when clicking delete
        setPlanToDelete(plan);
        setDeleteConfirmOpen(true);
    };

    const handleDeleteConfirm = async () => {
        if (planToDelete) {
            try {
                await deleteUserPlan(planToDelete.id);
                setDeleteConfirmOpen(false);
                setPlanToDelete(null);
                loadUserActivePlans(); // Reload the plans after deletion
            } catch (error) {
                console.error('Error deleting plan:', error);
            }
        }
    };

    const handleDeleteCancel = () => {
        setDeleteConfirmOpen(false);
        setPlanToDelete(null);
    };

    // Edit mode handlers
    const handleEdit = (e, row) => {
        e.stopPropagation(); // Prevent row expansion when clicking edit
        setEditRowId(row.id);
        setEditData({
            sessionsLeft: row.sessionsLeft || '',
            startDate: toDateInputValue(row.purchasedAt),
            endDate: row.endDate ? new Date(row.endDate).toISOString().split('T')[0] : '',
            paymentHoliday: row.paymentHoliday || false,
        });
    };

    const handleChange = (field, value) => {
        setEditData({ ...editData, [field]: value });
    };

    const handleSaveEdit = async (e) => {
        e.stopPropagation(); // Prevent row expansion when clicking save

        const plan = plans.find((item) => item.id === editRowId);
        const body = {
            sessionsLeft: Number(editData.sessionsLeft),
            endDate: editData.endDate ? new Date(editData.endDate) : null,
            paymentHoliday: editData.paymentHoliday,
        };

        if (!planHasContract(plan) && editData.startDate) {
            body.purchasedAt = new Date(editData.startDate);
        }

        await updateUserPlan(editRowId, body);
        setEditRowId(null);
        loadUserActivePlans();
    };

    const handleCancelEdit = (e) => {
        e.stopPropagation(); // Prevent row expansion when clicking cancel
        setEditRowId(null);
    };

    return (
        <Box>
            <Typography variant="h5" gutterBottom sx={{
                ml: 2,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
            }}>
                Active Plans
                {role === 'affiliate' && (
                    <Button
                        variant="contained"
                        onClick={handleOpenModal}
                        startIcon={<AddIcon />}
                        sx={{ mr: 2 }}
                    >
                        Add Plan
                    </Button>
                )}
            </Typography>

            <Paper sx={{ overflowX: 'auto' }}>
                {plans.length === 0 ? (
                    <Box sx={{ p: 3, textAlign: 'center' }}>
                        <Typography variant="body1">No active plans found.</Typography>
                    </Box>
                ) : (
                    <Table>
                        <TableHead>
                            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                                <TableCell>Plan Name</TableCell>
                                <TableCell>Price</TableCell>
                                <TableCell>Sessions Left</TableCell>
                                {role === 'affiliate' && <TableCell>Actions</TableCell>}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {plans.map((plan) => {
                                const isOpen = openRow === plan.id;
                                const isEditing = editRowId === plan.id;
                                const trainingTypes = parseTrainingTypes(plan.trainingType);
                                const daysLeft = calculateDaysLeft(plan);
                                const scheduleLines = formatRegistrationScheduleForDisplay(
                                    plan.hasRegistrationSchedule,
                                    plan.registrationSchedule
                                );

                                return (
                                    <React.Fragment key={plan.id}>
                                        {/* Main Row */}
                                        <TableRow
                                            hover
                                            onClick={() => !isEditing && handleRowClick(plan.id)}
                                            sx={{ cursor: isEditing ? 'default' : 'pointer' }}
                                        >
                                            <TableCell>{plan.planName}</TableCell>

                                            {isEditing ? (
                                                <>
                                                    <TableCell sx={{ fontWeight: 'bold' }}>{formatPlanPrice(plan.price, plan)}</TableCell>
                                                    <TableCell>
                                                        <Chip
                                                            label={plan.sessionsLeft === 9999 ? "Unlimited" : plan.sessionsLeft}
                                                            color={plan.sessionsLeft > 5 ? "success" : plan.sessionsLeft > 0 ? "warning" : "error"}
                                                            size="small"
                                                        />
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography variant="body2" color="text.secondary">
                                                            Editing below...
                                                        </Typography>
                                                    </TableCell>
                                                </>
                                            ) : (
                                                <>
                                                    <TableCell sx={{ fontWeight: 'bold' }}>{formatPlanPrice(plan.price, plan)}</TableCell>
                                                    <TableCell>
                                                        <Chip
                                                            label={plan.sessionsLeft === 9999 ? "Unlimited" : plan.sessionsLeft}
                                                            color={plan.sessionsLeft > 5 ? "success" : plan.sessionsLeft > 0 ? "warning" : "error"}
                                                            size="small"
                                                        />
                                                    </TableCell>
                                                    {role === 'affiliate' && (
                                                        <TableCell>
                                                            <Box sx={{ display: 'flex', gap: 1 }}>
                                                                <Button
                                                                    size="small"
                                                                    variant="outlined"
                                                                    onClick={(e) => handleEdit(e, plan)}
                                                                >
                                                                    Edit
                                                                </Button>
                                                                <IconButton
                                                                    size="small"
                                                                    sx={{ color: 'error.main' }}
                                                                    onClick={(e) => handleDelete(e, plan)}
                                                                >
                                                                    <DeleteIcon />
                                                                </IconButton>
                                                            </Box>
                                                        </TableCell>
                                                    )}
                                                </>
                                            )}
                                        </TableRow>

                                        {/* Edit Row - Mobile responsive edit fields */}
                                        {isEditing && (
                                            <TableRow>
                                                <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={8}>
                                                    <Box sx={{
                                                        margin: { xs: 1, sm: 2 },
                                                        width: '100%',
                                                        maxWidth: '100%',
                                                        overflow: 'hidden'
                                                    }}>
                                                        <Typography variant="h6" gutterBottom component="div" sx={{ fontWeight: 'bold', mb: 2 }}>
                                                            Edit Plan Details
                                                        </Typography>

                                                        <Grid container spacing={{ xs: 1, sm: 2 }} sx={{ width: '100%' }}>
                                                            {!planHasContract(plan) && (
                                                                <Grid item xs={12} sm={plan.paymentHoliday ? 12 : 6}>
                                                                    <Typography variant="caption" sx={{ display: 'block', mb: 0.5, color: 'text.secondary' }}>
                                                                        Plan Start Date
                                                                    </Typography>
                                                                    <TextField
                                                                        type="date"
                                                                        size="small"
                                                                        fullWidth
                                                                        value={editData.startDate}
                                                                        onClick={(e) => e.stopPropagation()}
                                                                        onChange={(e) => handleChange('startDate', e.target.value)}
                                                                        sx={{ maxWidth: '100%' }}
                                                                    />
                                                                </Grid>
                                                            )}

                                                            <Grid item xs={12} sm={plan.paymentHoliday ? 12 : 6}>
                                                                <Typography variant="caption" sx={{ display: 'block', mb: 0.5, color: 'text.secondary' }}>
                                                                    Plan End Date
                                                                </Typography>
                                                                <TextField
                                                                    type="date"
                                                                    size="small"
                                                                    fullWidth
                                                                    value={editData.endDate}
                                                                    onClick={(e) => e.stopPropagation()}
                                                                    onChange={(e) => handleChange('endDate', e.target.value)}
                                                                    sx={{ maxWidth: '100%' }}
                                                                />
                                                            </Grid>

                                                            <Grid item xs={12} sm={plan.paymentHoliday ? 12 : 6}>
                                                                <Typography variant="caption" sx={{ display: 'block', mb: 0.5, color: 'text.secondary' }}>
                                                                    Remaining Sessions
                                                                </Typography>
                                                                <TextField
                                                                    type="number"
                                                                    size="small"
                                                                    fullWidth
                                                                    value={editData.sessionsLeft}
                                                                    onClick={(e) => e.stopPropagation()}
                                                                    onChange={(e) => handleChange('sessionsLeft', e.target.value)}
                                                                    sx={{ maxWidth: '100%' }}
                                                                />
                                                            </Grid>

                                                            {plan.paymentHoliday && (
                                                                <Grid item xs={12}>
                                                                    <Typography variant="caption" sx={{ display: 'block', mb: 0.5, color: 'text.secondary' }}>
                                                                        Payment Holiday
                                                                    </Typography>
                                                                    <FormControl size="small" sx={{ minWidth: 120, maxWidth: '100%' }}>
                                                                        <Select
                                                                            value={editData.paymentHoliday}
                                                                            onClick={(e) => e.stopPropagation()}
                                                                            onChange={(e) => handleChange('paymentHoliday', e.target.value)}
                                                                        >
                                                                            <MenuItem value={false}>No</MenuItem>
                                                                            <MenuItem value={true}>Yes</MenuItem>
                                                                        </Select>
                                                                    </FormControl>
                                                                </Grid>
                                                            )}

                                                            <Grid item xs={12}>
                                                                <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                                                                    <Button
                                                                        size="small"
                                                                        variant="contained"
                                                                        color="primary"
                                                                        onClick={handleSaveEdit}
                                                                    >
                                                                        Save
                                                                    </Button>
                                                                    <Button
                                                                        size="small"
                                                                        variant="outlined"
                                                                        onClick={handleCancelEdit}
                                                                    >
                                                                        Cancel
                                                                    </Button>
                                                                </Box>
                                                            </Grid>
                                                        </Grid>
                                                    </Box>
                                                </TableCell>
                                            </TableRow>
                                        )}

                                        {/* Detail Row */}
                                        <TableRow>
                                            <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={8}>
                                                <Collapse in={isOpen && !isEditing} timeout="auto" unmountOnExit>
                                                    <Box sx={{ margin: 2 }}>
                                                        <Typography variant="h6" gutterBottom component="div" sx={{ fontWeight: 'bold', mb: 3 }}>
                                                            Plan Details
                                                        </Typography>

                                                        <Grid container spacing={3}>
                                                            {/* Plan Overview Card */}
                                                            <Grid item xs={12} md={6}>
                                                                <Card elevation={1} sx={{ height: '100%' }}>
                                                                    <CardContent>
                                                                        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', color: '#2c3e50' }}>
                                                                            <FitnessCenterIcon sx={{ mr: 1 }} />
                                                                            Plan Overview
                                                                        </Typography>
                                                                        <Divider sx={{ mb: 2 }} />

                                                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                                                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                                                                <Typography variant="body1" sx={{ fontWeight: 'bold', mr: 1, minWidth: 140 }}>
                                                                                    Affiliate Name:
                                                                                </Typography>
                                                                                <Typography variant="body1">
                                                                                    {plan.affiliateName}
                                                                                </Typography>
                                                                            </Box>

                                                                            {plan.familyMember?.fullName && (
                                                                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                                                                    <Typography variant="body1" sx={{ fontWeight: 'bold', mr: 1, minWidth: 140 }}>
                                                                                        Family Member:
                                                                                    </Typography>
                                                                                    <Typography variant="body1">
                                                                                        {plan.familyMember.fullName}
                                                                                    </Typography>
                                                                                </Box>
                                                                            )}

                                                                            {plan.stebbyTicketCode && (
                                                                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                                                                    <Typography variant="body1" sx={{ fontWeight: 'bold', mr: 1, minWidth: 140 }}>
                                                                                        Stebby Ticket Code:
                                                                                    </Typography>
                                                                                    <Typography variant="body1">
                                                                                        {plan.stebbyTicketCode}
                                                                                    </Typography>
                                                                                </Box>
                                                                            )}

                                                                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                                                                <Typography variant="body1" sx={{ fontWeight: 'bold', mr: 1, minWidth: 140 }}>
                                                                                    Plan Name:
                                                                                </Typography>
                                                                                <Typography variant="body1">
                                                                                    {plan.planName}
                                                                                </Typography>
                                                                            </Box>

                                                                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                                                                <Typography variant="body1" sx={{ fontWeight: 'bold', mr: 1, minWidth: 140 }}>
                                                                                    Price:
                                                                                </Typography>
                                                                                <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                                                                                    {formatPlanPrice(plan.price, plan)}
                                                                                </Typography>
                                                                            </Box>

                                                                            <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1 }}>
                                                                                <Typography variant="body1" sx={{ fontWeight: 'bold', mr: 1, minWidth: 140 }}>
                                                                                    Training Types:
                                                                                </Typography>
                                                                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                                                    {trainingTypes.map((type, index) => (
                                                                                        <Chip
                                                                                            key={index}
                                                                                            label={type}
                                                                                            size="small"
                                                                                            color="primary"
                                                                                        />
                                                                                    ))}
                                                                                </Box>
                                                                            </Box>

                                                                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                                                                <Typography variant="body1" sx={{ fontWeight: 'bold', mr: 1, minWidth: 140 }}>
                                                                                    Validity Period:
                                                                                </Typography>
                                                                                <Typography variant="body1">
                                                                                    {getPlanValidityText(plan)}
                                                                                </Typography>
                                                                            </Box>

                                                                            {scheduleLines.length > 0 && (
                                                                                <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1 }}>
                                                                                    <Typography variant="body1" sx={{ fontWeight: 'bold', mr: 1, minWidth: 140 }}>
                                                                                        Reg. Schedule:
                                                                                    </Typography>
                                                                                    <Box>
                                                                                        {scheduleLines.map((line) => (
                                                                                            <Typography key={line} variant="body2">
                                                                                                {line}
                                                                                            </Typography>
                                                                                        ))}
                                                                                    </Box>
                                                                                </Box>
                                                                            )}

                                                                            {plan.paymentHoliday && (
                                                                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                                                                    <Typography variant="body1" sx={{ fontWeight: 'bold', mr: 1, minWidth: 140 }}>
                                                                                        Payment Holiday:
                                                                                    </Typography>
                                                                                    <Chip
                                                                                        label={plan.paymentHoliday ? "Yes" : "No"}
                                                                                        color={plan.paymentHoliday ? "warning" : "default"}
                                                                                        size="small"
                                                                                    />
                                                                                </Box>
                                                                            )}
                                                                        </Box>
                                                                    </CardContent>
                                                                </Card>
                                                            </Grid>

                                                            {/* Membership Status Card */}
                                                            <Grid item xs={12} md={6}>
                                                                <Card elevation={1} sx={{ height: '100%' }}>
                                                                    <CardContent>
                                                                        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', color: '#2c3e50' }}>
                                                                            <DateRangeIcon sx={{ mr: 1 }} />
                                                                            Membership Status
                                                                        </Typography>
                                                                        <Divider sx={{ mb: 2 }} />

                                                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                                                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                                                                <Typography variant="body1" sx={{ fontWeight: 'bold', mr: 1, minWidth: 140 }}>
                                                                                    Sessions Left:
                                                                                </Typography>
                                                                                <Chip
                                                                                    label={plan.sessionsLeft === 9999 ? "Unlimited" : plan.sessionsLeft}
                                                                                    color={plan.sessionsLeft > 5 ? "success" : plan.sessionsLeft > 0 ? "warning" : "error"}
                                                                                />
                                                                            </Box>

                                                                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                                                                <Typography variant="body1" sx={{ fontWeight: 'bold', mr: 1, minWidth: 140 }}>
                                                                                    Start Date:
                                                                                </Typography>
                                                                                <Typography variant="body1">
                                                                                    {formatDate(plan.purchasedAt)}
                                                                                </Typography>
                                                                            </Box>

                                                                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                                                                <Typography variant="body1" sx={{ fontWeight: 'bold', mr: 1, minWidth: 140 }}>
                                                                                    End Date:
                                                                                </Typography>
                                                                                <Typography variant="body1">
                                                                                    {formatDate(plan.endDate)}
                                                                                </Typography>
                                                                            </Box>

                                                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                                                <Typography variant="body1" sx={{ fontWeight: 'bold', mr: 1, minWidth: 140 }}>
                                                                                    Days Left:
                                                                                </Typography>
                                                                                <Chip
                                                                                    label={`${daysLeft} days`}
                                                                                    color={daysLeft > 14 ? "success" : daysLeft > 7 ? "warning" : "error"}
                                                                                    size="small"
                                                                                />
                                                                            </Box>
                                                                        </Box>
                                                                    </CardContent>
                                                                </Card>
                                                            </Grid>
                                                        </Grid>
                                                    </Box>
                                                </Collapse>
                                            </TableCell>
                                        </TableRow>
                                    </React.Fragment>
                                );
                            })}
                        </TableBody>
                    </Table>
                )}
            </Paper>

            {/* Modal for adding new plan */}
            <Dialog open={openModal} onClose={handleCloseModal} maxWidth="sm" fullWidth>
                <DialogTitle>Assign Plan to User</DialogTitle>
                <DialogContent sx={{ minWidth: 300, pt: 2 }}>
                    <Typography variant="body1" gutterBottom>
                        Choose one of your affiliate plans:
                    </Typography>

                    <FormControl fullWidth sx={{ mt: 1 }}>
                        <InputLabel>Select Plan</InputLabel>
                        <Select
                            value={selectedPlanId}
                            onChange={(e) => setSelectedPlanId(e.target.value)}
                            label="Select Plan"
                        >
                            <MenuItem value="">-- Please choose --</MenuItem>
                            {affiliatePlans.map((plan) => (
                                <MenuItem key={plan.id} value={plan.id}>
                                    {plan.name} ({formatPlanPrice(plan.price, plan)})
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={assignToChildren}
                                onChange={handleAssignToChildrenChange}
                                name="assignToChildren"
                            />
                        }
                        label="Assign to Children"
                        sx={{ mt: 2, display: 'block' }}
                    />

                    {assignToChildren && (
                        <FormControl fullWidth sx={{ mt: 2 }}>
                            <InputLabel>Select Family Member</InputLabel>
                            <Select
                                value={selectedFamilyMemberId}
                                onChange={(e) => setSelectedFamilyMemberId(e.target.value)}
                                label="Select Family Member"
                            >
                                <MenuItem value="">-- Please choose a family member --</MenuItem>
                                {familyMembers.map((member) => (
                                    <MenuItem key={member.id} value={member.id}>
                                        {member.fullName}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    )}

                    {assignToChildren && familyMembers.length === 0 && (
                        <Typography variant="body2" color="warning.main" sx={{ mt: 1 }}>
                            No family members found. Please add family members first.
                        </Typography>
                    )}

                    {/* Add plan start date checkbox */}
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={isPlanStartDate}
                                onChange={(e) => setIsPlanStartDate(e.target.checked)}
                                name="planStartDateCheckbox"
                            />
                        }
                        label="Set custom start date for plan"
                        sx={{ mt: 2, display: 'block' }}
                    />

                    {/* Add date picker that shows only when checkbox is checked */}
                    {isPlanStartDate && (
                        <FormControl fullWidth sx={{ mt: 2 }}>
                            <TextField
                                id="plan-start-date"
                                label="Plan Start Date"
                                type="date"
                                value={planStartDate}
                                onChange={(e) => setPlanStartDate(e.target.value)}
                                InputLabelProps={{
                                    shrink: true,
                                }}
                                inputProps={{
                                    min: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString().split('T')[0] // Minimum date is tomorrow
                                }}
                            />
                        </FormControl>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseModal}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleSaveModal}
                        disabled={
                            !selectedPlanId ||
                            (assignToChildren && !selectedFamilyMemberId) ||
                            (isPlanStartDate && !planStartDate)
                        }
                        startIcon={<AddIcon />}
                    >
                        Assign Plan
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Modal */}
            <Dialog open={deleteConfirmOpen} onClose={handleDeleteCancel} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ color: 'error.main' }}>Confirm Delete</DialogTitle>
                <DialogContent>
                    <Typography variant="body1">
                        Are you sure you want to delete the plan "{planToDelete?.planName}"?
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDeleteCancel}>Cancel</Button>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={handleDeleteConfirm}
                        startIcon={<DeleteIcon />}
                    >
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ActivePlans;